import { List } from 'lucide-react';
import { cn } from '../lib/utils';
import { useWorkflowBar } from '../contexts/WorkflowBarContext';
import { getOrgType, getUserOrganizationName } from '../authUtils';

const STEP_META = {
  ACKNOWLEDGE:     { width: 134, color: 'text-amber-700' },
  PROVIDE_INITIAL: { width: 136, color: 'text-sky-700' },
  OPEN_QUESTIONS:  { width: 136, color: 'text-indigo-700' },
  UNRESOLVED:      { width: 136, color: 'text-teal-700' },
  ONGOING_MONITOR: { width: 134, color: 'text-gray-900' },
};

const CLIP_FIRST = 'polygon(0% 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 0% 100%)';
const CLIP_MID   = 'polygon(0% 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 0% 100%, 10px 50%)';
const CLIP_LAST  = 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%, 10px 50%)';

const BORDER_COLOR = 'hsl(220 13% 86%)';

function WorkflowStatusBar() {
  const {
    counts, overdueCounts, totalReferrals, visible,
    assignedToFilter, setAssignedToFilter,
    workflowStepFilter, setWorkflowStepFilter,
    stepLabels,
  } = useWorkflowBar();

  if (!visible) return null;

  const totalOverdue = Object.values(overdueCounts).reduce((a, b) => a + b, 0);
  const allActive = workflowStepFilter === null;
  const orgType = getOrgType();
  const externalOrgLabel = orgType === 'TPA' || orgType === 'CARRIER'
    ? getUserOrganizationName()
    : null;

  return (
    <div data-testid="workflow-status-bar" className="flex items-center gap-10 h-[68px] font-sans text-xs">
      {/* Assigned To filter group */}
      <div className="flex h-full rounded-2xl border overflow-hidden" style={{ borderColor: BORDER_COLOR }}>
        {[
          { key: 'all',         label: 'All',         width: 'w-[70px]', icon: true },
          { key: 'tpa_carrier', label: externalOrgLabel || 'TPA/Carrier', width: 'min-w-[80px] max-w-[140px] px-3' },
          { key: 'smartlight',  label: 'SmartLight',  width: 'w-[80px]' },
        ].map((item, i) => (
          <button
            key={item.key}
            type="button"
            className={cn(
              'flex items-center justify-center gap-1 h-full transition-colors text-xs font-medium leading-tight',
              item.width,
              i > 0 && 'border-l',
              assignedToFilter === item.key
                ? 'bg-filter-bg-active text-filter-text-active'
                : 'bg-white text-foreground hover:bg-muted/50'
            )}
            style={i > 0 ? { borderColor: BORDER_COLOR } : undefined}
            onClick={() => setAssignedToFilter(item.key)}
          >
            {item.icon && <List className="h-3 w-3 shrink-0" />}
            {item.label}
          </button>
        ))}
      </div>

      {/* Workflow step buttons */}
      <div className="flex items-center h-full">
        {/* All */}
        <button
          type="button"
          className={cn(
            'flex flex-col items-center justify-start w-[80px] h-full rounded-l-2xl border border-r-0 pt-1.5 pb-0 font-normal transition-colors',
            allActive
              ? 'bg-filter-bg-active text-filter-text-active'
              : 'bg-white text-foreground hover:bg-muted/50'
          )}
          style={{ zIndex: allActive ? 10 : 1, position: 'relative', borderColor: BORDER_COLOR }}
          onClick={() => setWorkflowStepFilter(null)}
        >
          <div className="h-[32px] flex items-center justify-center gap-1">
            <List className="h-3.5 w-3.5" />
            <span>All</span>
          </div>
          <div className="h-[24px] flex items-center justify-center gap-1.5">
            <span className={cn(
              'min-w-[2rem] h-[1.1rem] rounded-md flex items-center justify-center px-1 text-[10px] tabular-nums',
              allActive ? 'bg-white/20 text-white' : 'bg-muted text-muted-foreground'
            )}>{totalReferrals}</span>
            {totalOverdue > 0 && (
              <span className="min-w-[2rem] h-[1.1rem] rounded-md flex items-center justify-center px-1 text-[10px] tabular-nums bg-destructive/10 text-destructive">
                {totalOverdue}
              </span>
            )}
          </div>
        </button>

        {/* Arrow step buttons */}
        {stepLabels.map((step, i) => {
          const isLast = i === stepLabels.length - 1;
          const isFirst = i === 0;
          const isActive = workflowStepFilter === step.key;
          const clipPath = isFirst ? CLIP_FIRST : isLast ? CLIP_LAST : CLIP_MID;
          const count = counts[step.key] ?? 0;
          const overdue = overdueCounts[step.key] ?? 0;
          const meta = STEP_META[step.key] ?? { width: 115, color: 'text-foreground' };
          return (
            <div
              key={step.key}
              className="relative -ml-[10px] shrink-0"
              style={{ width: `${meta.width}px`, height: '68px', zIndex: isActive ? 10 : i + 2 }}
            >
              {/* Border layer */}
              <div
                className={cn('absolute inset-0', isLast && 'rounded-r-2xl')}
                style={{ clipPath, backgroundColor: BORDER_COLOR }}
              />
              {/* Button inset 1px to reveal border layer */}
              <button
                type="button"
                data-step={step.key}
                className={cn(
                  'relative flex flex-col items-center justify-start pt-1.5 pb-0 transition-colors text-xs font-medium',
                  isLast && 'rounded-r-2xl',
                  isActive ? 'bg-filter-bg-active text-filter-text-active' : 'bg-white hover:bg-muted/50'
                )}
                style={{
                  clipPath,
                  margin: '1px',
                  width: 'calc(100% - 2px)',
                  height: 'calc(100% - 2px)',
                }}
                onClick={() => setWorkflowStepFilter(isActive ? null : step.key)}
              >
                <div className={cn(
                  'h-[32px] w-full flex items-center justify-center font-medium text-center leading-tight',
                  isFirst ? 'px-4' : 'pl-6 pr-4',
                  !isActive && meta.color
                )}>
                  {step.label.includes('/') ? (
                    <span className="text-center">{step.label.split('/')[0]}/<br />{step.label.split('/')[1]}</span>
                  ) : step.label}
                </div>
                <div className="h-[24px] w-full flex items-center justify-center gap-1.5">
                  <span
                    data-testid="step-count"
                    className={cn(
                      'min-w-[2rem] h-[1.1rem] rounded-md flex items-center justify-center px-1 text-[10px] tabular-nums',
                      isActive ? 'bg-white/20 text-white' : 'bg-muted text-muted-foreground'
                    )}
                  >{count}</span>
                  {overdue > 0 && (
                    <span className="min-w-[2rem] h-[1.1rem] rounded-md flex items-center justify-center px-1 text-[10px] tabular-nums bg-destructive/10 text-destructive">
                      {overdue}
                    </span>
                  )}
                </div>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export { WorkflowStatusBar };
